import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp, DollarSign, Plus } from "lucide-react";
import { DashboardCard } from "@/components/ui/dashboard-card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { CURRENCY_SYMBOLS, convertCurrency } from "@/lib/currencyConversion";
import { format, startOfMonth, endOfMonth } from "date-fns";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { motion, AnimatePresence } from "framer-motion";

interface IncomeEntry {
  id: string;
  amount: number;
  currency: string;
  source: string;
  category: string;
  income_date: string;
  is_recurring: boolean;
  recurrence_interval: string | null;
  note: string | null;
}

interface IncomeThisMonthProps {
  userId: string;
  refreshTrigger?: number;
  onAddIncome?: () => void;
}

export const IncomeThisMonth = ({ userId, refreshTrigger, onAddIncome }: IncomeThisMonthProps) => {
  const [income, setIncome] = useState<IncomeEntry[]>([]);
  const [totalIncome, setTotalIncome] = useState(0);
  const [currency, setCurrency] = useState("EUR");
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const loadIncome = async () => {
      if (!userId) return;
      
      setIsLoading(true);
      try {
        // Get user currency
        const { data: settings } = await supabase
          .from("budget_settings")
          .select("currency")
          .eq("user_id", userId)
          .maybeSingle();
        
        const userCurrency = settings?.currency || 'EUR';
        setCurrency(userCurrency);

        // Get current month's income
        const now = new Date();
        const monthStart = format(startOfMonth(now), "yyyy-MM-dd");
        const monthEnd = format(endOfMonth(now), "yyyy-MM-dd");

        const { data: incomeData, error } = await supabase
          .from("income")
          .select("*")
          .eq("user_id", userId)
          .gte("income_date", monthStart)
          .lte("income_date", monthEnd)
          .order("income_date", { ascending: false });

        if (error) {
          console.error("Error loading income:", error);
          return;
        }

        setIncome(incomeData || []);
        
        // Convert all income to user's currency for accurate total
        const total = (incomeData || []).reduce((sum, entry) => {
          return sum + convertCurrency(Number(entry.amount), entry.currency || 'EUR', userCurrency);
        }, 0);
        setTotalIncome(total);
      } catch (error) {
        console.error("Error loading income:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadIncome();
  }, [userId, refreshTrigger]);

  const currencySymbol = CURRENCY_SYMBOLS[currency as keyof typeof CURRENCY_SYMBOLS] || currency;

  // Don't show if no income
  if (!isLoading && income.length === 0) {
    return null;
  }

  if (isLoading) {
    return (
      <DashboardCard noPadding>
        <div className="p-4 animate-pulse flex items-center justify-between">
          <div className="h-5 bg-muted rounded w-1/3" />
          <div className="h-5 bg-muted rounded w-1/4" />
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard noPadding>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CollapsibleTrigger asChild>
          <button 
            className="w-full p-4 flex items-center justify-between hover:bg-accent/50 transition-colors"
            aria-expanded={isExpanded}
          >
            <div className="flex items-center gap-2">
              <span className="text-lg">💰</span>
              <span className="font-medium">Income:</span>
              <span className="text-primary font-semibold">
                {currencySymbol}{totalIncome.toFixed(2)}
              </span>
            </div>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="border-t"
              >
                <div className="p-4 space-y-2">
                  {income.map((entry) => {
                    const entrySymbol = CURRENCY_SYMBOLS[entry.currency as keyof typeof CURRENCY_SYMBOLS] || entry.currency;
                    return (
                      <button 
                        key={entry.id} 
                        onClick={() => {
                          // Dispatch custom event to scroll to this income in transaction list
                          window.dispatchEvent(new CustomEvent('scroll-to-income', { 
                            detail: { incomeId: entry.id } 
                          }));
                        }}
                        className="w-full flex items-center justify-between py-2 border-b border-border/50 last:border-0 hover:bg-accent/50 rounded-lg px-2 -mx-2 transition-colors cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <DollarSign className="w-4 h-4 text-primary" />
                          </div>
                          <div className="text-left">
                            <p className="text-sm font-medium">{entry.source}</p>
                            <p className="text-xs text-muted-foreground">
                              {format(new Date(entry.income_date), "MMM d")}
                            </p>
                          </div>
                        </div>
                        <span className="text-primary font-medium">
                          +{entrySymbol}{Number(entry.amount).toFixed(2)}
                        </span>
                      </button>
                    );
                  })}
                  
                  {/* Add Income Button */}
                  {onAddIncome && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="w-full mt-2 text-muted-foreground"
                      onClick={onAddIncome}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add income
                    </Button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CollapsibleContent>
      </Collapsible>
    </DashboardCard>
  );
};